import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiRequest } from '@/lib/queryClient';
import { ApiRequestError } from '@/lib/apiRequestError';
import { useToast } from '@/hooks/use-toast';

// ============================================================================
// Tipi
// ============================================================================

export interface UserProfile {
  id: number;
  username: string;
  fullName: string | null;
  firstName: string | null;
  lastName: string | null;
  email: string;
  jobTitle: string | null;
  phone: string | null;
  mobilePhone: string | null;
  officePhone: string | null;
  timezone: string | null;
  operationalCity: string | null;
  operationalCountry: string | null;
  language: string | null;
  avatar: string | null;
  linkedin: string | null;
  backupEmail: string | null;
  privateEmail: string | null;
  address: string | null;
  ownedDomains: string[];
}
export interface MartinaOperationalProfile { version:number;linkedinUrl:string;websiteUrl:string;ownedDomains:string[];roleSummary:string;crmPurpose:string;prioritySignals:string[];noiseSignals:string[];learningEnabled:boolean;learnedPreferences:Record<string,{score:number;evidenceCount:number;lastDecision:string;updatedAt:string}>;updatedAt:string; }

export interface UserEmailIdentity {
  id: number;
  companyId: number | null;
  jobTitle: string | null;
  relationshipType: 'owner' | 'consultant' | 'employee' | 'other' | null;
}

export interface UserEmailSignature {
  id: number;
  name: string;
  content: string | null;
  isDefault: boolean;
}

export interface UserEmail {
  id: number;
  email: string;
  displayName: string | null;
  isPrimary: boolean;
  status: string;
  isActive: boolean;
  syncEnabled: boolean;
  provider: string;
  protocol: string;
  imapHost: string | null;
  imapPort: number | null;
  imapSecure?: boolean | null;
  smtpHost: string | null;
  smtpPort: number | null;
  smtpSecure?: boolean | null;
  lastSyncAt: string | null;
  lastError: string | null;
  identity: UserEmailIdentity | null;
  signature: UserEmailSignature | null;
  type: 'primary' | 'active' | 'tracking-only' | 'inactive';
}

export interface UserIdentity {
  id: number;
  accountId: number | null;
  email: string;
  displayName: string | null;
  isPrimary: boolean;
  companyId: number | null;
  jobTitle: string | null;
  relationshipType: 'owner' | 'consultant' | 'employee' | 'other' | null;
  companyName: string | null;
  companyLogo: string | null;
  companyWebsite: string | null;
  accountEmail: string | null;
  accountIsActive: boolean | null;
}

export interface CreateEmailData {
  email: string;
  displayName?: string;
  password?: string;
  trackingOnly?: boolean;
  companyId?: number | null;
  newCompanyName?: string;
  jobTitle?: string | null;
  relationshipType?: string | null;
  imapHost?: string;
  imapPort?: number;
  imapSecure?: boolean;
  smtpHost?: string;
  smtpPort?: number;
  smtpSecure?: boolean;
}

export interface UpdateEmailData {
  displayName?: string;
  isPrimary?: boolean;
  syncEnabled?: boolean;
  isActive?: boolean;
  signatureHtml?: string | null;
  signatureText?: string | null;
  companyId?: number | null;
  jobTitle?: string | null;
  relationshipType?: string | null;
}

// ============================================================================
// Query Keys
// ============================================================================

const PROFILE_KEY = ['/api/users/me'];
const EMAILS_KEY = ['/api/users/emails'];
const IDENTITIES_KEY = ['/api/users/identities'];

// ============================================================================
// Hooks — Profilo
// ============================================================================

export function useUserProfile() {
  return useQuery<UserProfile>({ queryKey: PROFILE_KEY, queryFn: () => apiRequest('GET', '/api/users/me') });
}

export function useUpdateUserProfile() {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  return useMutation({
    mutationFn: (data: Partial<UserProfile>) => apiRequest('/api/users/me', { method: 'PATCH', data, notifyError: false }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: PROFILE_KEY });
      queryClient.invalidateQueries({ queryKey: ['/api/auth/me'] });
    },
    onError: (error) => {
      toast({
        title: 'Errore',
        description: error instanceof ApiRequestError && [422, 503].includes(error.status)
          ? error.message : 'Impossibile salvare il profilo.',
        variant: 'destructive',
      });
    },
  });
}
export function useMartinaOperationalProfile(){return useQuery<MartinaOperationalProfile>({queryKey:['/api/users/me/operational-profile'],queryFn:()=>apiRequest('GET','/api/users/me/operational-profile')});}
export function useUpdateMartinaOperationalProfile(){const queryClient=useQueryClient();return useMutation({mutationFn:(data:Partial<MartinaOperationalProfile>)=>apiRequest('PATCH','/api/users/me/operational-profile',data),onSuccess:()=>queryClient.invalidateQueries({queryKey:['/api/users/me/operational-profile']})});}

// ============================================================================
// Hooks — Email (NUOVO: unified view)
// ============================================================================

export function useUserEmails() {
  return useQuery<UserEmail[]>({ queryKey: EMAILS_KEY, queryFn: () => apiRequest('GET', '/api/users/emails') });
}

export function useCreateUserEmail() {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  return useMutation({
    mutationFn: (data: CreateEmailData) => apiRequest('POST', '/api/users/emails', data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: EMAILS_KEY });
      queryClient.invalidateQueries({ queryKey: ['/api/email-accounts'] });
      toast({ title: 'Email aggiunta' });
    },
    onError: (error: any) => {
      toast({ title: 'Errore', description: error?.message ?? 'Impossibile aggiungere l\'email.', variant: 'destructive' });
    },
  });
}

export function useUpdateUserEmail() {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  return useMutation({
    mutationFn: ({ id, ...data }: { id: number } & UpdateEmailData) =>
      apiRequest('PATCH', `/api/users/emails/${id}`, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: EMAILS_KEY });
      queryClient.invalidateQueries({ queryKey: ['/api/email-accounts'] });
    },
    onError: () => {
      toast({ title: 'Errore', description: 'Impossibile aggiornare l\'email.', variant: 'destructive' });
    },
  });
}

export function useDeleteUserEmail() {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  return useMutation({
    mutationFn: (id: number) => apiRequest('DELETE', `/api/users/emails/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: EMAILS_KEY });
      queryClient.invalidateQueries({ queryKey: ['/api/email-accounts'] });
      toast({ title: 'Email rimossa' });
    },
    onError: () => {
      toast({ title: 'Errore', description: 'Impossibile rimuovere l\'email.', variant: 'destructive' });
    },
  });
}

export function useAutoconfigEmail() {
  return useMutation({
    mutationFn: (id: number) => apiRequest('POST', `/api/users/emails/${id}/autoconfig`),
  });
}

// ============================================================================
// Hooks — Identità Aziendale (DEPRECATI: mantenuti per compatibilità)
// ============================================================================

export function useUserIdentities() {
  return useQuery<UserIdentity[]>({ queryKey: IDENTITIES_KEY, queryFn: () => apiRequest('GET', '/api/users/identities') });
}

export function useCreateUserEmailAlias() {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  return useMutation({
    mutationFn: (data: { email: string; displayName?: string }) =>
      apiRequest('POST', '/api/users/identities/alias', data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: IDENTITIES_KEY });
      toast({ title: 'Alias interno aggiunto' });
    },
    onError: (error: any) => {
      toast({ title: 'Errore', description: error?.message ?? 'Impossibile aggiungere alias.', variant: 'destructive' });
    },
  });
}

export function useCreateUserIdentity() {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  return useMutation({
    mutationFn: (data: {
      accountId: number;
      companyId?: number | null;
      newCompanyName?: string;
      jobTitle?: string | null;
      relationshipType?: string | null;
    }) => apiRequest('POST', '/api/users/identities', data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: IDENTITIES_KEY });
      queryClient.invalidateQueries({ queryKey: EMAILS_KEY });
    },
    onError: (error: any) => {
      toast({ title: 'Errore', description: error?.message ?? 'Impossibile aggiungere l\'identità.', variant: 'destructive' });
    },
  });
}

export function useUpdateUserIdentity() {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  return useMutation({
    mutationFn: ({ id, ...data }: { id: number; companyId?: number | null; jobTitle?: string | null; relationshipType?: string | null; isPrimary?: boolean }) =>
      apiRequest('PATCH', `/api/users/identities/${id}`, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: IDENTITIES_KEY });
      queryClient.invalidateQueries({ queryKey: EMAILS_KEY });
    },
    onError: () => {
      toast({ title: 'Errore', description: 'Impossibile aggiornare l\'identità.', variant: 'destructive' });
    },
  });
}

export function useDeleteUserIdentity() {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  return useMutation({
    mutationFn: (id: number) => apiRequest('DELETE', `/api/users/identities/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: IDENTITIES_KEY });
      queryClient.invalidateQueries({ queryKey: EMAILS_KEY });
    },
    onError: () => {
      toast({ title: 'Errore', description: 'Impossibile rimuovere l\'identità.', variant: 'destructive' });
    },
  });
}
